<template>
  <v-card class="pa-4" variant="flat">
    <div class="ncrm-em-timeline__header">
      <div class="ncrm-em-timeline__title">
        <v-icon size="15" class="mr-1" style="opacity: 0.62">mdi-chart-bar</v-icon>
        {{ $t("ncrm.emailMarketing.timeline.title") }}
        <span v-if="series.aggregatedBy === 'week'" class="ncrm-em-timeline__hint">
          {{ $t("ncrm.emailMarketing.timeline.weekly") }}
        </span>
      </div>
      <div class="ncrm-em-timeline__legend">
        <span class="ncrm-em-timeline__legend-item">
          <span class="ncrm-em-timeline__dot ncrm-em-timeline__dot--open" />
          {{ $t("ncrm.emailMarketing.timeline.open") }}
        </span>
        <span class="ncrm-em-timeline__legend-item">
          <span class="ncrm-em-timeline__dot ncrm-em-timeline__dot--click" />
          {{ $t("ncrm.emailMarketing.timeline.click") }}
        </span>
      </div>
    </div>

    <v-skeleton-loader v-if="loading && !series.hasData" type="image" height="120" />
    <svg
      v-else-if="chart"
      class="ncrm-em-timeline__svg"
      :viewBox="`0 0 ${chart.width} ${chart.height}`"
      preserveAspectRatio="none"
      role="img"
      :aria-label="$t('ncrm.emailMarketing.timeline.title')"
    >
      <line
        v-for="grid in chart.gridLines"
        :key="`grid-${grid.y}`"
        :x1="0"
        :x2="chart.width"
        :y1="grid.y"
        :y2="grid.y"
        class="ncrm-em-timeline__grid"
      />
      <text
        v-for="grid in chart.gridLines"
        :key="`label-${grid.y}`"
        :x="2"
        :y="grid.y - 3"
        class="ncrm-em-timeline__grid-label"
      >
        {{ grid.value }}
      </text>
      <g v-for="bar in chart.bars" :key="bar.key">
        <rect
          :x="bar.openX"
          :y="bar.openY"
          :width="bar.barWidth"
          :height="bar.openHeight"
          class="ncrm-em-timeline__bar--open"
          rx="1.5"
        />
        <rect
          :x="bar.clickX"
          :y="bar.clickY"
          :width="bar.barWidth"
          :height="bar.clickHeight"
          class="ncrm-em-timeline__bar--click"
          rx="1.5"
        />
        <text
          v-if="bar.showLabel"
          :x="bar.labelX"
          :y="chart.height - 4"
          text-anchor="middle"
          class="ncrm-em-timeline__day-label"
        >
          {{ bar.label }}
        </text>
      </g>
    </svg>
    <div v-else class="ncrm-em-timeline__empty">
      <v-icon size="22" style="opacity: 0.35">mdi-chart-bar</v-icon>
      <span>{{ $t("ncrm.emailMarketing.timeline.empty") }}</span>
    </div>
  </v-card>
</template>

<script>
import { buildTimelineSeries } from "@/Utile/ncrmEmailMarketing.mjs";

const CHART_W = 640;
const CHART_H = 130;
const PADDING_TOP = 8;
const PADDING_BOTTOM = 18;
const NICE_STEPS = [5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10000, 25000, 50000];

const niceMax = (value) => {
  if (value <= 0) return 5;
  const found = NICE_STEPS.find((step) => step >= value);
  return found || Math.ceil(value / 10000) * 10000;
};

export default {
  name: "NcrmEmailMarketingTimelineChart",
  props: {
    timeline: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  computed: {
    series() {
      return buildTimelineSeries(this.timeline);
    },
    chart() {
      const { days, hasData } = this.series;
      if (!hasData || !days.length) return null;
      const usableHeight = CHART_H - PADDING_TOP - PADDING_BOTTOM;
      const maxValue = niceMax(Math.max(...days.map((d) => Math.max(d.open, d.click))));
      const scale = (value) => {
        if (value <= 0) return 0;
        return Math.max(3, (value / maxValue) * usableHeight);
      };
      const groupWidth = CHART_W / days.length;
      const barWidth = Math.max(2, Math.min(14, groupWidth * 0.32));
      // Etichete de zi doar cat incap lizibil (max ~10 pe axa).
      const labelEvery = Math.max(1, Math.ceil(days.length / 10));
      const bars = days.map((entry, index) => {
        const groupCenter = groupWidth * index + groupWidth / 2;
        const openHeight = scale(entry.open);
        const clickHeight = scale(entry.click);
        const date = new Date(`${entry.day}T00:00:00`);
        return {
          key: entry.day,
          barWidth,
          openX: groupCenter - barWidth - 1,
          openY: CHART_H - PADDING_BOTTOM - openHeight,
          openHeight,
          clickX: groupCenter + 1,
          clickY: CHART_H - PADDING_BOTTOM - clickHeight,
          clickHeight,
          labelX: groupCenter,
          label: `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}`,
          showLabel: index % labelEvery === 0,
        };
      });
      const gridLines = [0, 0.5, 1].map((fraction) => ({
        y: CHART_H - PADDING_BOTTOM - usableHeight * fraction,
        value: Math.round(maxValue * fraction),
      }));
      return { width: CHART_W, height: CHART_H, bars, gridLines };
    },
  },
};
</script>

<style scoped>
.ncrm-em-timeline__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  border-bottom: 1px solid var(--ncrm-color-border-subtle);
  padding-bottom: 6px;
  margin-bottom: 10px;
}

.ncrm-em-timeline__title {
  font-size: 0.82rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--app-secondary-button-text, rgb(var(--v-theme-secondary)));
  display: flex;
  align-items: center;
  gap: 4px;
}

.ncrm-em-timeline__hint {
  text-transform: none;
  letter-spacing: normal;
  font-size: 0.72rem;
  opacity: 0.6;
}

.ncrm-em-timeline__legend {
  display: flex;
  gap: 12px;
  font-size: 0.76rem;
  opacity: 0.75;
}

.ncrm-em-timeline__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.ncrm-em-timeline__dot {
  width: 9px;
  height: 9px;
  border-radius: var(--app-radius-pill);
  display: inline-block;
}

.ncrm-em-timeline__dot--open,
.ncrm-em-timeline__bar--open {
  background: rgb(var(--v-theme-info));
  fill: rgb(var(--v-theme-info));
}

.ncrm-em-timeline__dot--click,
.ncrm-em-timeline__bar--click {
  background: rgb(var(--v-theme-success));
  fill: rgb(var(--v-theme-success));
}

.ncrm-em-timeline__svg {
  width: 100%;
  height: 130px;
  display: block;
}

.ncrm-em-timeline__grid {
  stroke: rgba(var(--v-theme-on-surface), 0.08);
  stroke-width: 1;
}

.ncrm-em-timeline__grid-label,
.ncrm-em-timeline__day-label {
  font-size: 8px;
  fill: rgba(var(--v-theme-on-surface), 0.45);
}

.ncrm-em-timeline__empty {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  opacity: 0.5;
  padding: 24px 0;
}
</style>
